/**
 * Mapa de rotas da aplicação.
 *
 * Papel: este é o arquivo onde ficam declaradas todas as rotas da aplicação,
 * dentro do array que é passado para o `createBrowserRouter` logo abaixo. Uma
 * tela só consegue ser acessada na aplicação se ela for adicionada na
 * propriedade `children`, sendo que o `path` deverá ser o endereço que a pessoa
 * digita no navegador e o `Component` deverá ser a página que será mostrada
 * quando esse endereço for acessado.
 *
 * Como usar: para que a tela de um módulo passe a ter endereço próprio, será
 * preciso acrescentar mais um objeto dentro de `children`, seguindo o formato
 * abaixo. A página apontada pelo `Component` deverá ser importada pelo
 * `index.ts` público do módulo, e nunca por um caminho interno dele, porque o
 * ESLint recusa esse tipo de importação entre um módulo e outro.
 *
 * ```tsx
 * import { ComponenteDaRota } from '@/modules/nome-do-modulo';
 *
 * { path: 'nome-da-rota', Component: ComponenteDaRota }
 * ```
 *
 * A rota que tem `path: '*'` é a que casa com qualquer endereço que não tenha
 * sido encontrado nas anteriores, e por causa disso ela deverá permanecer
 * sempre como o último item do array. Qualquer objeto que for acrescentado
 * depois dela nunca chegará a ser alcançado, e a tela correspondente ficaria
 * inacessível sem que nada acusasse o erro.
 *
 * A rota `/` é a que monta o `LayoutProtegido`, e todo objeto que estiver
 * dentro de `children` é filho dela. Por isso, uma tela nova já passa a exigir
 * login sem que seja preciso acrescentar nenhuma proteção a ela.
 *
 * Existe um segundo grupo protegido, irmão da rota `/`: o do
 * `DocumentoProtegido`, com as páginas de documento que abrem numa aba nova e
 * vão para o papel, sem o `Layout` em volta. Uma página que deverá aparecer
 * sem cabeçalho nem menu entra nos `children` desse grupo, com o caminho
 * inteiro desde a raiz.
 */
import { createBrowserRouter } from 'react-router';

import { PaginaCadastrosBase } from '@/modules/cadastros-base';
import { PaginaDocumentos } from '@/modules/documentos';
import { PaginaMailing } from '@/modules/mailing';
import { PaginaCalendario } from '@/modules/calendario';
import { PaginaCompras, PaginaFormularioPedido } from '@/modules/compras';
import { PaginaFinancas, PaginaOrdemDePagamento, PaginaRecibo } from '@/modules/financas';
import { PaginaManutencoes } from '@/modules/manutencoes';
import { PaginaFormularioOrcamento, PaginaOrcamentos } from '@/modules/orcamentos';
import { PaginaResidencias } from '@/modules/residencias';
import { PaginaRh } from '@/modules/rh';
import { PaginaSaude } from '@/modules/saude';
import { PaginaVeiculos } from '@/modules/veiculos';
import { PaginaUsuarios } from '@/modules/usuarios';
import { DocumentoProtegido, LayoutProtegido } from './rota-protegida';
import { RotaDeAdministrador } from './rota-administrador';
import { PaginaInicial } from './pagina-inicial';
import { PaginaNaoEncontrada } from './pagina-nao-encontrada';

export const router = createBrowserRouter([
  {
    path: '/',
    Component: LayoutProtegido,
    children: [
      { index: true, Component: PaginaInicial },
      { path: 'mailing', Component: PaginaMailing },
      { path: 'cadastros-base', Component: PaginaCadastrosBase },
      { path: 'documentos', Component: PaginaDocumentos },
      { path: 'calendario', Component: PaginaCalendario },
      { path: 'compras', Component: PaginaCompras },
      { path: 'compras/novo', Component: PaginaFormularioPedido },
      { path: 'compras/:id', Component: PaginaFormularioPedido },
      { path: 'financas', Component: PaginaFinancas },
      { path: 'manutencoes', Component: PaginaManutencoes },
      { path: 'orcamentos', Component: PaginaOrcamentos },
      { path: 'orcamentos/novo', Component: PaginaFormularioOrcamento },
      { path: 'orcamentos/:id', Component: PaginaFormularioOrcamento },
      { path: 'residencias', Component: PaginaResidencias },
      { path: 'rh', Component: PaginaRh },
      { path: 'saude', Component: PaginaSaude },
      { path: 'veiculos', Component: PaginaVeiculos },
      {
        Component: RotaDeAdministrador,
        children: [{ path: 'usuarios', Component: PaginaUsuarios }],
      },
      { path: '*', Component: PaginaNaoEncontrada },
    ],
  },
  {
    Component: DocumentoProtegido,
    children: [
      { path: 'financas/recibo/lancamento/:lancamentoId', Component: PaginaRecibo },
      {
        path: 'financas/recibo/outros-bancos/:favorecidoId/:competencia',
        Component: PaginaRecibo,
      },
      {
        path: 'financas/ordem-de-pagamento/:favorecidoId/:competencia',
        Component: PaginaOrdemDePagamento,
      },
    ],
  },
]);
